import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound, Mail } from 'lucide-react';
import { LoginForm } from '../../components/auth/LoginForm';
import { clearError, setError } from '../../redux/auth/authSlice';
import firebaseService from '../../services/firebase/firebaseService';
import { RootState } from '../../redux/store';
import Image from '../../components/common/Image';
import { TaskyAuthLayout, TaskyAlert, TaskyButton } from '../../components/tasky';
import { useMetaLang, AUTH_STRINGS, otherLang, toggleLabel } from '../../i18n/metaReviewLang';
import { getPostAuthDestination, hasOrganization } from '../../utils/authDestination';
import { applyAuthResult } from '../../utils/applyAuthResult';
import { consumeReturnTo } from '../../utils/returnTo';
import { behavior } from '../../analytics';

/**
 * サーバーが 403 で返した理由（所属組織の無効化など）。
 * 汎用の「メールアドレスとパスワードをご確認ください」では本人が何も直せないため、そのまま見せる。
 */
const forbiddenMessage = (e: any): string | null => {
  const message = e?.response?.data?.message;
  return e?.response?.status === 403 && typeof message === 'string' && message ? message : null;
};

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [lang, setLang] = useMetaLang();
  const t = AUTH_STRINGS[lang];
  const { isAuthenticated, user, isLoading, error } = useSelector(
    (state: RootState) => state.auth,
  );
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState('');
  const [isMagicLinkSending, setIsMagicLinkSending] = useState(false);

  // ログイン済みユーザーの着地先は SignupPage / CompleteSignInPage と同じ
  // getPostAuthDestination で決める（組織未所属なら /signup/setup。判定の根拠と
  // user が null の過渡状態を待つ理由は utils/authDestination.ts のコメント参照）。
  // パスワードログインと Google ログインはどちらもこの effect を通る
  // (handleLogin / handleGoogleLogin は navigate せず applyAuthResult するだけ)。
  //
  // 認証切れで /login に飛ばされた場合は元いた画面（utils/returnTo）へ戻す。
  // StrictMode では effect が 2 回走るため、1 回目で consumeReturnTo() が戻り先を
  // 消費し、2 回目が /home に上書きしてしまう。遷移は 1 回だけに固定する。
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user) return;
    if (hasRedirectedRef.current) return;

    // 組織未所属ユーザーは会社情報入力が先。戻り先は消費せず残しておく。
    // 組織の有無は getPostAuthDestination / PrivateRoute と同じ hasOrganization で判定する
    // （ズレると片方が /signup/setup へ送り、もう片方が通す矛盾が起きる）。
    if (hasOrganization(user)) {
      const returnTo = consumeReturnTo();
      if (returnTo) {
        hasRedirectedRef.current = true;
        navigate(returnTo, { replace: true });
        return;
      }
    }

    hasRedirectedRef.current = true;
    navigate(getPostAuthDestination(user));
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleLogin = async (credentials: { email: string; password: string }) => {
    dispatch(clearError());
    setPasswordlessLoginEmail('');
    try {
      const result = await firebaseService.loginWithEmailAndPassword(
        credentials.email,
        credentials.password
      );
      if (result && result.user && result.token) {
        applyAuthResult(dispatch, { token: result.token, user: result.user });
      }
    } catch (e: any) {
      console.error('Login failed:', e);
      // 失敗理由を残す（/login の離脱が多いのに Firebase の失敗コードが追えなかった）。
      // メールアドレス・パスワードは絶対に props に入れない。
      behavior.action('login_failed', { method: 'password', code: e?.code ?? 'unknown' });
      const fallbackMessage = t.errLoginFailed;
      if (e?.code === 'auth/invalid-credential') {
        setPasswordlessLoginEmail(credentials.email.trim());
      }
      dispatch(setError(lang === 'en' ? fallbackMessage : forbiddenMessage(e) ?? (e?.response ? fallbackMessage : e?.message || fallbackMessage)));
    }
  };

  const handleSendMagicLinkLogin = async () => {
    if (!passwordlessLoginEmail) return;

    setIsMagicLinkSending(true);
    try {
      await firebaseService.sendMagicLink(passwordlessLoginEmail);
      navigate('/auth/magic-link-sent');
    } catch (e) {
      console.error('Magic link login failed:', e);
      dispatch(setError(t.errMagicLink));
    } finally {
      setIsMagicLinkSending(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    dispatch(clearError());
    setPasswordlessLoginEmail('');
    try {
      const result = await firebaseService.signInWithSocial('google');
      if (result && result.user && result.token) {
        applyAuthResult(dispatch, { token: result.token, user: result.user });
      }
    } catch (e: any) {
      console.error('Google login failed:', e);
      behavior.action('login_failed', { method: 'google', code: e?.code ?? 'unknown' });
      if (e?.code === 'auth/popup-closed-by-user') return;
      if (e?.code === 'auth/popup-blocked') {
        dispatch(setError(t.errPopupBlocked));
      } else if (e?.code === 'auth/account-exists-with-different-credential') {
        dispatch(setError(t.errDifferentCredential));
      } else {
        dispatch(setError(lang === 'en' ? t.errGoogleLogin : forbiddenMessage(e) ?? t.errGoogleLogin));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <TaskyAuthLayout
      logo={
        <Image category="common" fileName="logo.png" alt="Logo" className="h-10 w-auto" />
      }
      description={t.tagline}
    >
      <button
        type="button"
        onClick={() => setLang(otherLang(lang))}
        title="Language"
        className="fixed top-4 right-4 z-50 px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-warm-border rounded-full hover:bg-warm-hover transition-colors"
      >
        🌐 {toggleLabel(lang)}
      </button>
      {error && (
        <TaskyAlert tone="error" className="mb-5">
          <div className="space-y-3">
            <div>{error}</div>
            {passwordlessLoginEmail && (
              <>
                {/* パスワード誤りの後に再設定へ進んだのは 65 セッション中 4 件だけで、
                    画面下の小さなリンクに気付かれていなかった。エラーの場所で案内する。 */}
                <p className="text-sm">{t.invalidCredentialHint}</p>
                <div className="flex flex-wrap gap-2">
                  <TaskyButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate('/forgot-password', { state: { email: passwordlessLoginEmail } })
                    }
                  >
                    <KeyRound />
                    {t.resetPassword}
                  </TaskyButton>
                  <TaskyButton
                    type="button"
                    variant="outline"
                    size="sm"
                    isLoading={isMagicLinkSending}
                    onClick={handleSendMagicLinkLogin}
                  >
                    <Mail />
                    {isMagicLinkSending ? t.sending : t.magicLinkLogin}
                  </TaskyButton>
                </div>
              </>
            )}
          </div>
        </TaskyAlert>
      )}

      <LoginForm onSubmit={handleLogin} isLoading={isLoading} />

      <div className="flex items-center my-6">
        <div className="flex-1 h-px bg-warm-border"></div>
        <span className="px-3 text-xs text-gray-400">{t.or}</span>
        <div className="flex-1 h-px bg-warm-border"></div>
      </div>

      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={isGoogleLoading || isLoading}
        className="w-full bg-white hover:bg-warm-hover border border-warm-border rounded-tasky-sm py-2.5 text-sm font-medium text-gray-700 flex items-center justify-center gap-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" className="flex-shrink-0">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {isGoogleLoading ? t.loggingIn : t.googleLogin}
      </button>

      <p className="text-center text-sm text-gray-500 mt-6">
        <Link
          to="/forgot-password"
          className="text-primary hover:text-primary-hover font-medium transition-colors"
        >
          {t.forgotPasswordLink}
        </Link>
      </p>
    </TaskyAuthLayout>
  );
};

export default LoginPage;
